/**
 * Gift Builder - Build Your Gift Page JavaScript
 * Handles product selection, summary updates, progress tracking, and cart submission
 */

class GiftBuilder {
  constructor() {
    this.form = document.getElementById('gift-builder-form');
    this.submitButton = document.getElementById('gift-builder-submit');
    this.addItemsButton = document.getElementById('gift-builder-add-items');
    this.bundleVariantInput = document.getElementById('bundle-variant-id');
    this.giftWrapVariantInput = document.getElementById('gift-wrap-variant-id');
    this.giftWrapProductTitle = document.getElementById('gift-wrap-product-title');
    this.messageInput = document.getElementById('gift-message');
    this.messageCharCount = document.getElementById('message-char-count');
    this.totalElement = document.getElementById('gift-builder-total');

    this.selections = {
      collectionX: null,
      collectionY: null,
      message: ''
    };

    if (!this.form || !this.submitButton) {
      console.warn('Gift Builder: Required elements not found');
      return;
    }

    this.init();
  }

  init() {
    this.bindEvents();
    this.updateButtonState();
    this.bindViewMoreButtons();
    this.initProgressIndicator();
    this.setInitialActiveStep();
    this.initSearch();
  }

  bindEvents() {
    // Collection X product selection
    const collectionXInputs = this.form.querySelectorAll('input[name="collection_x_product"]');
    collectionXInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleProductSelection(e, 'collectionX', 'x'));
    });

    // Collection Y product selection
    const collectionYInputs = this.form.querySelectorAll('input[name="collection_y_product"]');
    collectionYInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleProductSelection(e, 'collectionY', 'y'));
    });

    // Variant selectors
    const variantSelects = this.form.querySelectorAll('.gift-builder__variant-select');
    variantSelects.forEach(select => {
      select.addEventListener('change', (e) => this.handleVariantChange(e));
    });

    // Personal message input
    if (this.messageInput) {
      this.messageInput.addEventListener('input', (e) => this.handleMessageInput(e));
      this.messageInput.addEventListener('blur', () => this.updateStepStates());
    }

    // Form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Add items button click
    if (this.addItemsButton) {
      this.addItemsButton.addEventListener('click', (e) => this.handleAddItems(e));
    }
  }

  setInitialActiveStep() {
    const step1 = this.form.querySelector('[data-step="1"]');
    if (step1) {
      step1.classList.add('active');
    }
    this.updateProgressIndicator();
  }

  handleProductSelection(event, selectionKey, summaryKey) {
    const input = event.target;
    const card = input.closest('.gift-builder__product-card');
    const productTitle = input.dataset.productTitle;
    const productHandle = input.value;

    // Check if there's a variant selector for this product
    const variantSelect = card.querySelector('.gift-builder__variant-select');
    let variantId = input.dataset.variantId;
    let variantTitle = '';
    let variantImage = null;
    let variantPrice = parseInt(input.dataset.variantPrice, 10) || 0;

    if (variantSelect) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      variantId = selectedOption.value;
      variantTitle = selectedOption.dataset.variantTitle || '';
      variantImage = selectedOption.dataset.variantImage;
      variantPrice = parseInt(selectedOption.dataset.variantPrice, 10) || 0;
    }

    // Update product image to show the selected variant's image
    if (variantImage) {
      this.updateProductImage(card, variantImage);
    }

    // Build display title with variant
    const displayTitle = variantTitle ? `${productTitle} - ${variantTitle}` : productTitle;

    this.selections[selectionKey] = {
      title: productTitle,
      variantTitle: variantTitle,
      displayTitle: displayTitle,
      handle: productHandle,
      variantId: variantId,
      price: variantPrice
    };

    this.updateSummary(summaryKey, displayTitle);
    this.updateTotal();
    this.updateButtonState();
    this.updateProgressIndicator();

    // Hide other products in the same collection
    this.hideOtherProducts(input);

    // Update step states
    this.updateStepStates(selectionKey);

    // Auto-scroll to next step after a brief delay
    setTimeout(() => {
      this.scrollToNextStep(selectionKey);
    }, 300);
  }

  handleVariantChange(event) {
    const select = event.target;
    const collection = select.dataset.collection;
    const card = select.closest('.gift-builder__product-card');
    const input = card.querySelector('input[type="radio"]');

    const selectedOption = select.options[select.selectedIndex];
    const variantId = selectedOption.value;
    const variantTitle = selectedOption.dataset.variantTitle || '';
    const variantImage = selectedOption.dataset.variantImage;
    const variantPrice = parseInt(selectedOption.dataset.variantPrice, 10) || 0;
    const productTitle = input.dataset.productTitle;

    // Always update product image when variant changes (even if product not selected)
    if (variantImage) {
      this.updateProductImage(card, variantImage);
    }

    // Only update selection state if this product is selected
    if (!input.checked) return;

    const selectionKey = collection === 'x' ? 'collectionX' : 'collectionY';
    const summaryKey = collection;

    // Build display title with variant
    const displayTitle = variantTitle ? `${productTitle} - ${variantTitle}` : productTitle;

    // Update selection
    this.selections[selectionKey] = {
      title: productTitle,
      variantTitle: variantTitle,
      displayTitle: displayTitle,
      handle: input.value,
      variantId: variantId,
      price: variantPrice
    };

    this.updateSummary(summaryKey, displayTitle);
    this.updateTotal();
  }

  updateProductImage(card, imageUrl) {
    const productImage = card.querySelector('.gift-builder__product-image img');
    if (!productImage || !imageUrl) return;

    // Don't update if the image is already the same
    if (productImage.src === imageUrl) return;

    // Preload the new image then swap instantly
    const newImage = new Image();
    newImage.onload = () => {
      productImage.src = imageUrl;
    };
    newImage.src = imageUrl;
  }

  updateTotal() {
    if (!this.totalElement) return;

    // Base bundle price is £20
    // If a variant costing £14.99 is selected, total becomes £23
    const premiumVariantPrice = 1499; // £14.99 in pence
    let total = 2000; // Base £20 in pence

    const priceX = this.selections.collectionX?.price || 0;
    const priceY = this.selections.collectionY?.price || 0;

    if (priceX === premiumVariantPrice || priceY === premiumVariantPrice) {
      total = 2300; // £23 in pence
    }

    // Convert from pence to pounds and format
    const totalPounds = total / 100;
    this.totalElement.textContent = `£${totalPounds.toFixed(2)}`;
  }

  handleMessageInput(event) {
    const message = event.target.value;
    this.selections.message = message;

    // Update character count
    if (this.messageCharCount) {
      const length = message.length;
      this.messageCharCount.textContent = length;

      // Update counter styling based on length
      const counter = this.messageCharCount.closest('.gift-builder__message-counter');
      if (counter) {
        counter.classList.remove('near-limit', 'at-limit');
        if (length >= 255) {
          counter.classList.add('at-limit');
        } else if (length >= 200) {
          counter.classList.add('near-limit');
        }
      }
    }

    // Update summary
    this.updateSummary('message', message ? 'Added' : 'None');
    this.updateProgressIndicator();
  }

  updateStepStates(completedSelectionKey) {
    const step1 = this.form.querySelector('[data-step="1"]');
    const step2 = this.form.querySelector('[data-step="2"]');
    const step3 = this.form.querySelector('[data-step="3"]');

    // Reset all states
    [step1, step2, step3].forEach(step => {
      if (step) {
        step.classList.remove('active', 'completed');
      }
    });

    // Set states based on selections
    if (this.selections.collectionX) {
      step1.classList.add('completed');
      if (!this.selections.collectionY) {
        step2.classList.add('active');
      }
    } else {
      step1.classList.add('active');
    }

    if (this.selections.collectionY) {
      step2.classList.add('completed');
      if (step3) {
        step3.classList.add('active');
      }
    }

    // Step 3 is completed if message has been entered (optional)
    if (this.selections.collectionX && this.selections.collectionY && this.selections.message) {
      if (step3) {
        step3.classList.add('completed');
      }
    }
  }

  scrollToNextStep(completedSelectionKey) {
    let targetStep = null;

    if (completedSelectionKey === 'collectionX' && !this.selections.collectionY) {
      targetStep = this.form.querySelector('[data-step="2"]');
    } else if (completedSelectionKey === 'collectionY') {
      // Scroll to step 3 (message) when item 2 is selected
      targetStep = this.form.querySelector('[data-step="3"]');
    }

    if (targetStep) {
      targetStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  hideOtherProducts(selectedInput) {
    const inputName = selectedInput.name;
    const allInputs = this.form.querySelectorAll(`input[name="${inputName}"]`);

    allInputs.forEach(input => {
      const card = input.closest('.gift-builder__product-card');
      if (card) {
        if (input === selectedInput) {
          card.classList.add('selected');
          card.classList.remove('hidden');
        } else {
          card.classList.add('hidden');
        }
      }
    });

    // Add change button if not exists
    this.addChangeButton(selectedInput);
  }

  addChangeButton(selectedInput) {
    const inputName = selectedInput.name;
    const step = selectedInput.closest('.gift-builder__step');
    const productsContainer = step.querySelector('.gift-builder__products');

    // Remove existing change button if any
    const existingBtn = step.querySelector('.gift-builder__change-btn');
    if (existingBtn) existingBtn.remove();

    // Create change button
    const changeBtn = document.createElement('button');
    changeBtn.type = 'button';
    changeBtn.className = 'gift-builder__change-btn';
    changeBtn.textContent = 'Change Selection';
    changeBtn.addEventListener('click', () => this.showAllProducts(inputName, step));

    productsContainer.parentNode.insertBefore(changeBtn, productsContainer.nextSibling);
  }

  showAllProducts(inputName, step) {
    const allInputs = this.form.querySelectorAll(`input[name="${inputName}"]`);
    const viewMoreBtn = step.querySelector('.gift-builder__view-more-btn');

    allInputs.forEach(input => {
      const card = input.closest('.gift-builder__product-card');
      if (card) {
        // Check if this card was initially hidden and view more wasn't clicked
        const isInitiallyHidden = card.classList.contains('initially-hidden');
        const viewMoreClicked = viewMoreBtn && viewMoreBtn.style.display === 'none';

        // Only show initially hidden cards if view more was clicked
        if (isInitiallyHidden && !viewMoreClicked) {
          return;
        }

        card.classList.remove('hidden', 'selected');
      }
    });

    // Remove change button
    const changeBtn = step.querySelector('.gift-builder__change-btn');
    if (changeBtn) changeBtn.remove();
  }

  bindViewMoreButtons() {
    const viewMoreBtns = document.querySelectorAll('.gift-builder__view-more-btn');
    viewMoreBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleViewMore(e));
    });
  }

  handleViewMore(event) {
    const btn = event.target;
    const step = btn.closest('.gift-builder__step');
    const hiddenCards = step.querySelectorAll('.gift-builder__product-card.initially-hidden');

    // Show all hidden cards
    hiddenCards.forEach(card => {
      card.classList.remove('initially-hidden');
    });

    // Hide the View More button
    btn.style.display = 'none';
  }

  updateSummary(key, value) {
    const summaryItem = this.form.querySelector(`[data-summary="${key}"]`);
    const summaryValue = summaryItem?.querySelector('.gift-builder__summary-value');

    if (summaryValue) {
      summaryValue.textContent = value;

      if (key === 'x' || key === 'y') {
        const placeholder = summaryValue.dataset.placeholder;
        if (value && value !== placeholder) {
          summaryValue.classList.add('selected');
        } else {
          summaryValue.classList.remove('selected');
        }
      }

      if (key === 'message') {
        if (value === 'Added') {
          summaryValue.classList.add('selected');
        } else {
          summaryValue.classList.remove('selected');
        }
      }
    }

    // Update summary all-selected state
    this.updateSummaryState();
  }

  updateSummaryState() {
    const summary = this.form.querySelector('.gift-builder__summary');
    if (summary) {
      if (this.selections.collectionX && this.selections.collectionY) {
        summary.classList.add('all-selected');
      } else {
        summary.classList.remove('all-selected');
      }
    }
  }

  updateButtonState() {
    const isValid = this.selections.collectionX && this.selections.collectionY;
    const hasBundleProduct = this.bundleVariantInput && this.bundleVariantInput.value;

    this.submitButton.disabled = !(isValid && hasBundleProduct);

    // Add items button only requires selections, not the bundle product
    if (this.addItemsButton) {
      this.addItemsButton.disabled = !isValid;
    }
  }

  // Progress Indicator
  initProgressIndicator() {
    this.progressSteps = document.querySelectorAll('[data-progress-step]');
    this.progressLines = document.querySelectorAll('[data-progress-line]');
  }

  updateProgressIndicator() {
    if (!this.progressSteps.length) return;

    const step1 = this.progressSteps[0];
    const step2 = this.progressSteps[1];
    const step3 = this.progressSteps[2];
    const line1 = this.progressLines[0];
    const line2 = this.progressLines[1];

    // Reset all
    this.progressSteps.forEach(step => {
      step.classList.remove('active', 'completed');
    });
    this.progressLines.forEach(line => {
      line.classList.remove('filled');
    });

    // Step 1 (Collection X)
    if (this.selections.collectionX) {
      step1.classList.add('completed');
      if (line1) line1.classList.add('filled');

      if (!this.selections.collectionY) {
        step2.classList.add('active');
      }
    } else {
      step1.classList.add('active');
    }

    // Step 2 (Collection Y)
    if (this.selections.collectionY) {
      step2.classList.add('completed');
      if (line2) line2.classList.add('filled');

      if (step3) {
        step3.classList.add('active');
      }
    }

    // Step 3 (Message - optional, complete when message entered)
    if (this.selections.collectionX && this.selections.collectionY && this.selections.message && step3) {
      step3.classList.add('completed');
    }
  }

  // ===== Live Search =====
  initSearch() {
    this.searchInputs = document.querySelectorAll('.gift-builder__search-input');
    this.searchDebounceTimers = {};

    this.searchInputs.forEach(input => {
      const collection = input.dataset.searchInput;

      input.addEventListener('input', () => {
        clearTimeout(this.searchDebounceTimers[collection]);
        this.searchDebounceTimers[collection] = setTimeout(() => {
          this.filterProducts(collection, input.value);
        }, 200);

        // Toggle clear button visibility
        const clearBtn = document.querySelector(`[data-search-clear="${collection}"]`);
        if (clearBtn) {
          clearBtn.style.display = input.value.length > 0 ? 'flex' : 'none';
        }
      });

      // Prevent form submission on Enter
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
      });
    });

    // Clear buttons
    document.querySelectorAll('.gift-builder__search-clear').forEach(btn => {
      btn.addEventListener('click', () => {
        const collection = btn.dataset.searchClear;
        const input = document.querySelector(`[data-search-input="${collection}"]`);
        if (input) {
          input.value = '';
          btn.style.display = 'none';
          this.filterProducts(collection, '');
          input.focus();
        }
      });
    });
  }

  filterProducts(collection, query) {
    const step = this.form.querySelector(`[data-step="${collection === 'x' ? '1' : '2'}"]`);
    if (!step) return;

    const cards = step.querySelectorAll('.gift-builder__product-card');
    const viewMoreBtn = step.querySelector('.gift-builder__view-more-btn');
    const countEl = document.querySelector(`[data-search-count="${collection}"]`);
    const searchTerm = query.trim().toLowerCase();

    let visibleCount = 0;
    let totalCount = cards.length;

    cards.forEach(card => {
      // Don't interfere with selected/hidden state from product selection
      if (card.classList.contains('selected') || card.classList.contains('hidden')) return;

      const title = card.dataset.productTitle || '';
      const matches = !searchTerm || title.includes(searchTerm);

      if (matches) {
        card.classList.remove('search-hidden');
        visibleCount++;
      } else {
        card.classList.add('search-hidden');
      }
    });

    // Update count display
    if (countEl) {
      if (searchTerm) {
        countEl.textContent = `${visibleCount} of ${totalCount} items found`;
        countEl.style.display = 'block';
      } else {
        countEl.textContent = '';
        countEl.style.display = 'none';
      }
    }

    // When searching, show all matching products (bypass initially-hidden)
    if (searchTerm && viewMoreBtn) {
      cards.forEach(card => {
        if (!card.classList.contains('search-hidden')) {
          card.classList.remove('initially-hidden');
        }
      });
    }
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.selections.collectionX || !this.selections.collectionY) {
      this.showError('Please select one item from each collection');
      return;
    }

    const bundleVariantId = this.bundleVariantInput?.value;
    if (!bundleVariantId) {
      this.showError('Bundle product not configured. Please contact support.');
      return;
    }

    this.setLoadingState(true);

    try {
      await this.addToCart(bundleVariantId);
      this.redirectToCart();
    } catch (error) {
      console.error('Gift Builder: Error adding to cart', error);
      this.showError('Failed to add to cart. Please try again.');
      this.setLoadingState(false);
    }
  }

  async addToCart(variantId) {
    const giftWrapTitle = this.giftWrapProductTitle?.value || 'Gift Wrap';

    const lineItemProperties = {
      '_gift_bundle': 'true',
      'Selected Item 1 (Collection X)': this.selections.collectionX.displayTitle || this.selections.collectionX.title,
      'Item 1 Handle': this.selections.collectionX.handle,
      'Item 1 Variant ID': this.selections.collectionX.variantId,
      'Selected Item 2 (Collection Y)': this.selections.collectionY.displayTitle || this.selections.collectionY.title,
      'Item 2 Handle': this.selections.collectionY.handle,
      'Item 2 Variant ID': this.selections.collectionY.variantId,
      'Gift Wrap': 'Included',
      'Delivery': 'Included'
    };

    // Add personal message if provided
    if (this.selections.message) {
      lineItemProperties['Personal Message'] = this.selections.message;
    }

    const items = [{
      id: parseInt(variantId, 10),
      quantity: 1,
      properties: lineItemProperties
    }];

    // Add gift wrap product if configured
    const giftWrapVariantId = this.giftWrapVariantInput?.value;
    if (giftWrapVariantId) {
      items.push({
        id: parseInt(giftWrapVariantId, 10),
        quantity: 1,
        properties: {
          '_gift_bundle_wrap': 'true',
          'Bundle Item': 'Gift Wrap'
        }
      });
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.description || 'Failed to add to cart');
    }

    // Update cart note with personal message if provided
    if (this.selections.message) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          note: this.selections.message
        })
      });
    }

    return response.json();
  }

  async handleAddItems(event) {
    event.preventDefault();

    if (!this.selections.collectionX || !this.selections.collectionY) {
      this.showError('Please select one item from each collection');
      return;
    }

    this.setAddItemsLoadingState(true);

    try {
      await this.addSelectedItemsToCart();
      this.redirectToCart();
    } catch (error) {
      console.error('Gift Builder: Error adding items to cart', error);
      this.showError('Failed to add items to cart. Please try again.');
      this.setAddItemsLoadingState(false);
    }
  }

  async addSelectedItemsToCart() {
    const orderTag = 'Bundle + Gift Wrap';

    const items = [
      {
        id: parseInt(this.selections.collectionX.variantId, 10),
        quantity: 1,
        properties: {
          '_bundle_item': 'true',
          '_order_tag': orderTag,
          'Bundle Type': orderTag
        }
      },
      {
        id: parseInt(this.selections.collectionY.variantId, 10),
        quantity: 1,
        properties: {
          '_bundle_item': 'true',
          '_order_tag': orderTag,
          'Bundle Type': orderTag
        }
      }
    ];

    // Add gift wrap product if configured
    const giftWrapVariantId = this.giftWrapVariantInput?.value;
    if (giftWrapVariantId) {
      items.push({
        id: parseInt(giftWrapVariantId, 10),
        quantity: 1,
        properties: {
          '_bundle_item': 'true',
          '_order_tag': orderTag,
          'Bundle Type': 'Gift Wrap'
        }
      });
    }

    // Add items to cart
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.description || 'Failed to add to cart');
    }

    // Set cart attributes for order tagging and note
    const attributes = {
      'Order Tag': orderTag
    };

    const updateBody = { attributes };

    // Add personal message as order note if provided
    if (this.selections.message) {
      updateBody.note = this.selections.message;
    }

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(updateBody)
    });

    return response.json();
  }

  setAddItemsLoadingState(isLoading) {
    if (!this.addItemsButton) return;

    if (isLoading) {
      this.addItemsButton.classList.add('loading');
      this.addItemsButton.disabled = true;
    } else {
      this.addItemsButton.classList.remove('loading');
      this.updateButtonState();
    }
  }

  redirectToCart() {
    window.location.href = '/cart';
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.submitButton.classList.add('loading');
      this.submitButton.disabled = true;
    } else {
      this.submitButton.classList.remove('loading');
      this.updateButtonState();
    }
  }

  showError(message) {
    // Check if error element already exists
    let errorEl = this.form.querySelector('.gift-builder__form-error');

    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'gift-builder__form-error gift-builder__error';
      this.submitButton.parentNode.insertBefore(errorEl, this.submitButton);
    }

    errorEl.textContent = message;
    errorEl.style.display = 'block';

    // Auto-hide after 5 seconds
    setTimeout(() => {
      errorEl.style.display = 'none';
    }, 5000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new GiftBuilder());
} else {
  new GiftBuilder();
}
